import { CoreState, DeploymentState, enumID2Name } from 'common/CommonConstants';
import type { CoreInstanceType } from 'core/CoreInstanceConstants';

// TODO: Lot's of boilerplace here :(

export enum EventType {
  CORE_PERFORMANCE = 2020,
  CORE_STATE_CHANGE = 2010,
  CORE_FIRST_GREEN_STATUS = 2030,
  DEPLOYMENT_CREATE = 1000,
  DEPLOYMENT_STATE_CHANGE = 1010,
  DEPLOYMENT_RESIZE = 1011,
  DEPLOYMENT_SAVE = 1020,
  DEPLOYMENT_FIRST_GREEN_STATUS = 1030,
  DEPLOYMENT_FIRST_LOAD = 1040,
  DEPLOYMENT_BROWSER_IDE = 1050,
  DEPLOYMENT_BROWSER_IDE_INACTIVE = 1051,
  DEPLOYMENT_HOST_OPEN = 1060,
  DEPLOYMENT_HOST_OPEN_INACTIVE = 1061,
  DEPLOYMENT_SSH_OPEN = 1062,
  DEPLOYMENT_SSH_OPEN_INACTIVE = 1063,

  PROJECT_SAVE = 4000,
  PROJECT_CREATE_CLICK = 4001,
  STORAGE_CREATE = 3000,
  //STORAGE_RESIZE = 3010, // Not implemented yet
  STORAGE_DELETE = 3020,
  INVITE = 5000,
  THANK_YOU = 5001,
  TRIAL_WAIT = 5002,
  TRIAL_CREATE = 5003,
  TRIAL_CREATE_INTERNAL = 5005,
  TRIAL_TUTORIAL_DISMISS = 5004,
  TRIAL_CLAIM_START = 5006, // NOTE! Added in april 2025

  LOGIN_START = 5100, // NOTE! Added in april 2025
  LOGIN = 5101, // NOTE! Added in april 2025
  LOGIN_ERROR = 5102, // NOTE! Added in april 2025

  PERFORMANCE_TEST = 6000,
  ACCOUNT_PAYMENT = 6100,
}
export const eventTypeName = enumID2Name<EventType>(EventType);

/** These events are allowed to be logged from the client */
export const ClientSideEvents = [
  EventType.DEPLOYMENT_BROWSER_IDE_INACTIVE,
  EventType.DEPLOYMENT_HOST_OPEN,
  EventType.DEPLOYMENT_HOST_OPEN_INACTIVE,
  EventType.TRIAL_TUTORIAL_DISMISS,
  EventType.DEPLOYMENT_SSH_OPEN_INACTIVE,
] as const;

export enum FirstGreenStatusItem {
  STORAGE = 1,
  BUILDS = 2,
  CONTAINERS = 3,
  SERVICES = 4,
  SETUP = 5,
  DEPLOYMENT = 6,
  CORE = 7,
}
export const firstGreenStatusItemName = enumID2Name<FirstGreenStatusItem>(FirstGreenStatusItem);

//
// Core events
//

export type CoreFirstGreenStatusEvent = {
  eventTypeID: EventType.CORE_FIRST_GREEN_STATUS;
  timeCreate: string;
  firstGreenStatusItemID: FirstGreenStatusItem;
  coreID: number;
  deploymentID: number;
};
export type CoreFirstGreenStatusData = Omit<CoreFirstGreenStatusEvent, 'eventTypeID' | 'timeCreate'>;

export type CorePerformanceEvent = {
  eventTypeID: EventType.CORE_PERFORMANCE;
  timeCreate: string;
  coreID: number;
  accountID?: number;
  deploymentID?: number;
  action: string;
  data: unknown;
};
export type CorePerformanceData = Omit<CorePerformanceEvent, 'eventTypeID' | 'timeCreate'>;

export type CoreStateChangeEvent = {
  eventTypeID: EventType.CORE_STATE_CHANGE;
  timeCreate: string;
  coreID: number;
  deploymentID?: number;
  coreStateID: CoreState;
  coreInstanceTypeID?: CoreInstanceType; // When launching
  reason:
    | 'admin'
    | 'core-scheduler'
    | 'prewarm-launch'
    | 'user-launch'
    | 'user-launch-prewarmed'
    | 'public-launch'
    | 'public-launch-prewarmed'
    | 'socket'
    | 'delete-complete'
    | 'initialize-return';
  accountID?: number;
  userID?: number;
  isConnected?: boolean;
};
export type CoreStateChangeData = Omit<CoreStateChangeEvent, 'eventTypeID' | 'timeCreate'>;

//
// Deployment events
//
export type DeploymentCreateEvent = {
  eventTypeID: EventType.DEPLOYMENT_CREATE;
  timeCreate: string;
  deploymentID: number;
  accountID: number;
  userID: number;
  coreInstanceTypeID: CoreInstanceType;
};
export type DeploymentCreateData = Omit<DeploymentCreateEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentFirstLoadEvent = {
  eventTypeID: EventType.DEPLOYMENT_FIRST_LOAD;
  timeCreate: string;
  deploymentID: number;
  accountID: number;
  userID: number;
};
export type DeploymentFirstLoadData = Omit<DeploymentFirstLoadEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentFirstGreenStatusEvent = {
  eventTypeID: EventType.DEPLOYMENT_FIRST_GREEN_STATUS;
  timeCreate: string;
  firstGreenStatusItemID: FirstGreenStatusItem;
  deploymentID: number;
};
export type DeploymentFirstGreenStatusData = Omit<DeploymentFirstGreenStatusEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentStateChangeEvent = {
  eventTypeID: EventType.DEPLOYMENT_STATE_CHANGE;
  timeCreate: string;
  deploymentID: number;
  deploymentStateID: DeploymentState;
  coreInstanceTypeID?: CoreInstanceType; // When launching
  reason:
    | 'admin'
    | 'anonymous-cleanup'
    | 'auto-stop'
    | 'delete-complete'
    | 'enable-from-url'
    | 'user-create'
    | 'user-clone'
    | 'user-enable'
    | 'user-disable'
    | 'user-delete'
    | 'admin-delete-failed-core';
  accountID: number;
  userID?: number;
};
export type DeploymentStateChangeData = Omit<DeploymentStateChangeEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentResizeEvent = {
  eventTypeID: EventType.DEPLOYMENT_RESIZE;
  timeCreate: string;
  deploymentID: number;
  coreInstanceTypeID: CoreInstanceType;
  reason: 'user';
  accountID: number;
  userID?: number;
};
export type DeploymentResizeData = Omit<DeploymentResizeEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentBrowserIdeEvent = {
  eventTypeID: EventType.DEPLOYMENT_FIRST_GREEN_STATUS;
  timeCreate: string;
  deploymentID: number;
  userID: number;
};
export type DeploymentBrowserIdeData = Omit<DeploymentBrowserIdeEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentBrowserIdeInactiveEvent = {
  eventTypeID: EventType.DEPLOYMENT_FIRST_GREEN_STATUS;
  timeCreate: string;
  deploymentID: number;
  userID: number;
  reason: 'disabled' | 'starting';
};
export type DeploymentBrowserIdeInactiveData = Omit<DeploymentBrowserIdeInactiveEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentHostOpenEvent = {
  eventTypeID: EventType.DEPLOYMENT_HOST_OPEN;
  timeCreate: string;
  deploymentID: number;
  userID: number;
  host: string;
  reason: 'open' | 'force';
};
export type DeploymentHostOpenData = Omit<DeploymentHostOpenEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentHostOpenInactiveEvent = {
  eventTypeID: EventType.DEPLOYMENT_HOST_OPEN_INACTIVE;
  timeCreate: string;
  deploymentID: number;
  userID: number;
  host: string;
  reason: 'disabled' | 'starting';
};
export type DeploymentHostOpenInactiveData = Omit<DeploymentHostOpenInactiveEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentSshOpenInactiveEvent = {
  eventTypeID: EventType.DEPLOYMENT_SSH_OPEN_INACTIVE;
  timeCreate: string;
  deploymentID: number;
  userID: number;
  item: 'command' | 'container' | 'vscode';
};
export type DeploymentSshOpenInactiveData = Omit<DeploymentSshOpenInactiveEvent, 'eventTypeID' | 'timeCreate'>;

export type DeploymentSaveEvent = {
  eventTypeID: EventType.DEPLOYMENT_SAVE;
  timeCreate: string;
  status: string;
  deploymentID: number;
  userID: number;
};
export type DeploymentSaveData = Omit<DeploymentSaveEvent, 'eventTypeID' | 'timeCreate'>;

//
// Invite and thank you events
//

export type InviteEvent = {
  eventTypeID: EventType.INVITE;
  timeCreate: string;
  description: 'page-load';
  accountID?: number;
  userID?: number;
  token: string;
  email: string;
};
export type InviteData = Omit<InviteEvent, 'eventTypeID' | 'timeCreate'>;

export type ThankYouEvent = {
  eventTypeID: EventType.THANK_YOU;
  email: string;
  timeCreate: string;
};
export type ThankYouData = Omit<ThankYouEvent, 'eventTypeID' | 'timeCreate'>;

//
// Trial events
//

export type TrialCreateEvent = {
  eventTypeID: EventType.TRIAL_CREATE;
  timeCreate: string;
  componentIds: number[];
  accountID: number;
  projectID: number;
  deploymentID: number;
  userID: number;
  utm: { [service: string]: string } | null;
};
export type TrialCreateData = Omit<TrialCreateEvent, 'eventTypeID' | 'timeCreate'>;

export type TrialClaimStartEvent = {
  eventTypeID: EventType.TRIAL_CLAIM_START;
  timeCreate: string;
  accountID: number;
  userID: number;
};
export type TrialClaimStartData = Omit<TrialClaimStartEvent, 'eventTypeID' | 'timeCreate'>;

export type TrialTutorialDismissEvent = {
  eventTypeID: EventType.TRIAL_TUTORIAL_DISMISS;
  timeCreate: string;
  deploymentID: number;
  userID: number;
};
export type TrialTutorialDismissData = Omit<TrialTutorialDismissEvent, 'eventTypeID' | 'timeCreate'>;

export type TrialWaitEvent = {
  eventTypeID: EventType.TRIAL_WAIT;
  timeCreate: string;
  timeUpdate: string;
  deploymentID: number;
  userID: number;
};
export type TrialWaitData = Omit<TrialWaitEvent, 'eventTypeID' | 'timeCreate' | 'timeUpdate'>;

//
// Login Events
//

export type LoginStartEvent = {
  eventTypeID: EventType.LOGIN_START;
  timeCreate: string;
  path: string | null;
};
export type LoginStartData = Omit<LoginStartEvent, 'eventTypeID' | 'timeCreate'>;

export type LoginEvent = {
  eventTypeID: EventType.LOGIN;
  timeCreate: string;
  userID: number;
};
export type LoginData = Omit<LoginEvent, 'eventTypeID' | 'timeCreate'>;

export type LoginErrorEvent = {
  eventTypeID: EventType.LOGIN_ERROR;
  timeCreate: string;
  userID: number | null;
  reason: string;
};
export type LoginErrorData = Omit<LoginErrorEvent, 'eventTypeID' | 'timeCreate'>;

//
// Project events
//

export type ProjectSaveEvent = {
  eventTypeID: EventType.PROJECT_SAVE;
  timeCreate: string;
  status: string;
  projectID: number;
  userID: number;
};
export type ProjectSaveData = Omit<ProjectSaveEvent, 'eventTypeID' | 'timeCreate'>;

export type ProjectCreateClickEvent = {
  eventTypeID: EventType.PROJECT_CREATE_CLICK;
  timeCreate: string;
  userID: number;
};
export type ProjectCreateClickData = Omit<ProjectCreateClickEvent, 'eventTypeID' | 'timeCreate'>;

//
// Storage events
//

export type StorageCreateEvent = {
  eventTypeID: EventType.STORAGE_CREATE;
  timeCreate: string;
  deploymentID: number;
  accountID: number;
  storageSizeGiB: number;
  storageVolumeID: string;
};
export type StorageCreateData = Omit<StorageCreateEvent, 'eventTypeID' | 'timeCreate'>;

export type StorageDeleteEvent = {
  eventTypeID: EventType.STORAGE_DELETE;
  timeCreate: string;
  deploymentID: number;
  accountID: number;
  storageVolumeID: string;
};
export type StorageDeleteData = Omit<StorageDeleteEvent, 'eventTypeID' | 'timeCreate'>;

//
// Misc
//

export type PerformanceTestEvent = {
  eventTypeID: EventType.PERFORMANCE_TEST;
  timeCreate: string;
  performanceTestID: number;
  action: string;
  data: unknown;
};
export type PerformanceTestData = Omit<PerformanceTestEvent, 'eventTypeID' | 'timeCreate'>;

// AccountPaymentEvent
export type AccountPaymentEvent = {
  eventTypeID: EventType.ACCOUNT_PAYMENT;
  timeCreate: string;
  sum: number;
  accountID: number;
  reason: 'new-account-free-credits' | 'manual-payment' | 'free-credits' | 'free-credits';
  userID?: number; // User who payed
  adminUserID?: number; // Admin user who assigned free-credits
};
export type AccountPaymentData = Omit<AccountPaymentEvent, 'eventTypeID' | 'timeCreate'>;

export type Event =
  | CoreFirstGreenStatusEvent
  | CorePerformanceEvent
  | CoreStateChangeEvent
  | DeploymentCreateEvent
  | DeploymentFirstGreenStatusEvent
  | DeploymentSaveEvent
  | DeploymentStateChangeEvent
  | StorageCreateEvent
  | StorageDeleteEvent
  | PerformanceTestEvent
  | AccountPaymentEvent;
